import { useCallback, useEffect, useRef, useState } from "react";
import { Bird } from "lucide-react";
import { SpotifyPlayer } from "@/SpotifyPlayer";
import { AudioToggle } from "@/AudioToggle";
import { birdFlags } from "@/birdsong";

const useTrackTitle = (uri) => {
  const [title, setTitle] = useState("");
  useEffect(() => {
    const m = uri?.match(/^spotify:(track|episode):(\w+)$/);
    if (!m) return;
    let alive = true;
    fetch(`https://open.spotify.com/oembed?url=https://open.spotify.com/${m[1]}/${m[2]}`)
      .then((r) => r.json())
      .then((j) => { if (alive) { setTitle(j.title || ""); window.__nowPlaying = j.title; } })
      .catch(() => {});
    return () => { alive = false; };
  }, [uri]);
  return title;
};

const useMusicOnEntry = (controllerRef, ready) => {
  const done = useRef(false);
  useEffect(() => {
    if (!ready) return;
    const play = () => {
      const c = controllerRef.current;
      if (done.current || !c) return;
      if (c.__playing) { done.current = true; return; }
      try { c.play(); window.__musicAutoTried = (window.__musicAutoTried || 0) + 1; } catch { /* blocked */ }
    };
    const check = setTimeout(() => { if (controllerRef.current?.__playing) done.current = true; }, 2500);
    const onGesture = (e) => {
      if (e.target.closest?.('[data-testid="bird-button"], [data-testid="spotify-embed"]')) { done.current = true; return; }
      play();
      done.current = true;
      remove();
    };
    const remove = () => {
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
    };
    play();
    window.addEventListener("pointerdown", onGesture, true);
    window.addEventListener("keydown", onGesture, true);
    return () => { clearTimeout(check); remove(); };
  }, [ready, controllerRef]);
};

export const AudioCluster = ({ controllerRef, initialUri, onWake }) => {
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [uri, setUri] = useState(null);
  const title = useTrackTitle(uri);
  const onPlayback = useCallback((p, playingUri) => {
    setPlaying(p);
    birdFlags.on = p;
    if (playingUri) setUri(playingUri);
    if (controllerRef.current) controllerRef.current.__playing = p;
  }, [controllerRef]);
  const toggle = () => {
    try { controllerRef.current?.togglePlay(); } catch { /* not ready */ }
  };
  useMusicOnEntry(controllerRef, ready);

  return (
    <div className="audio-cluster" data-testid="audio-cluster" onPointerEnter={onWake}>
      <SpotifyPlayer controllerRef={controllerRef} initialUri={initialUri} onPlayback={onPlayback} onReady={setReady} />
      <div className="audio-row">
        <AudioToggle on={playing} onClick={ready ? toggle : undefined} testId="bird-button" ariaLabel="Bird: Spotify music on or off"
          icon={<Bird size={14} />} label={ready ? "bird" : "bird…"} />
        <a className="spotify-login" href="https://accounts.spotify.com/login" target="_blank" rel="noopener noreferrer" data-testid="spotify-login-link">
          Spotify login
        </a>
      </div>
      <div className="now-playing" data-playing={playing} data-testid="now-playing">
        <span className="np-label">{playing ? "now playing" : "paused"}</span>
        <span className="np-title" data-testid="now-playing-title">{title || "—"}</span>
      </div>
    </div>
  );
};
